/**
 * Utility để xử lý URL media cho sản phẩm
 * Backend serve từ /api/public/ endpoint
 */

type MediaFolder = 'images' | 'videos' | 'zips' | 'guides'

const MEDIA_FOLDERS: MediaFolder[] = ['images', 'videos', 'zips', 'guides']

const EXTENSION_MAP: Record<MediaFolder, string[]> = {
  images: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico'],
  videos: ['mp4', 'webm', 'ogg', 'avi', 'mov', 'wmv', 'mkv'],
  zips: ['zip'],
  guides: ['pdf', 'doc', 'docx', 'txt']
}

const inferFolderByExtension = (path: string): MediaFolder => {
  const extension = path.split('.').pop()?.toLowerCase() || ''

  const matchedEntry = Object.entries(EXTENSION_MAP).find(([, extensions]) =>
    extensions.includes(extension)
  )

  return (matchedEntry?.[0] as MediaFolder) || 'images'
}

const stripDomain = (value: string) => value.replace(/^https?:\/\/[^/]+/i, '')

const splitPathAndQuery = (value: string) => {
  const [pathPart, ...queryParts] = value.split('?')
  return {
    path: pathPart,
    query: queryParts.length ? `?${queryParts.join('?')}` : ''
  }
}

const ensurePublicPath = (rawPath: string): string => {
  let path = stripDomain(rawPath).replace(/\\/g, '/')
  path = path.replace(/^\/+/, '')

  if (!path) {
    return ''
  }

  // Remove 'api/' prefix if present (will add it back later)
  if (path.startsWith('api/')) {
    path = path.replace(/^api\//, '')
  }

  // Keep 'public/' prefix - backend serves from /api/public/
  if (path.startsWith('public/')) {
    const afterPublic = path.slice('public/'.length)

    if (!afterPublic) {
      return 'public'
    }

    // If already has correct folder structure, return with public prefix
    if (MEDIA_FOLDERS.some(folder => afterPublic.startsWith(`${folder}/`))) {
      return path // Keep 'public/images/...'
    }

    // Otherwise, infer folder and add it
    const folder = inferFolderByExtension(afterPublic)
    return `public/${folder}/${afterPublic}`
  }

  // If path already starts with a media folder, add public prefix
  if (MEDIA_FOLDERS.some(folder => path.startsWith(`${folder}/`))) {
    return `public/${path}`
  }

  // Otherwise, infer folder and add it with public prefix
  const folder = inferFolderByExtension(path)
  return `public/${folder}/${path}`
}

export const buildProductMediaUrl = (input?: string, fallback: string = ''): string => {
  if (!input) {
    return fallback
  }

  const trimmed = input.trim()

  // Check if it's a full URL
  const isFullUrl = /^https?:\/\//i.test(trimmed)
  
  // If it's a full URL and already has /api/public/, return as is
  if (isFullUrl && trimmed.includes('/api/public/')) {
    return trimmed
  }

  // Extract path from full URL or use as-is for relative paths
  const { path, query } = splitPathAndQuery(trimmed)
  const relativePath = ensurePublicPath(path)

  if (!relativePath) {
    return fallback
  }

  // Backend serves static files from /api/public/ endpoint
  // Use API base URL to construct full URL
  const baseUrl =
    process.env.NEXT_PUBLIC_API_URL?.replace('/api', '') || 'http://localhost:7001'

  const normalizedBase = baseUrl.replace(/\/+$/, '')
  const normalizedPath = relativePath.replace(/^\/+/, '')

  // Return /api/public/images/... format
  return `${normalizedBase}/api/${normalizedPath}${query}`
}

